import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { RingLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../services/api';
import SizePicker from '../components/SizePicker';
import { formatPrice } from '../utils/currency';
import './CheckoutPage.css';
import { trackVisit } from '../services/visitTrackingService';

const CheckoutPage = () => {
  const { t } = useTranslation();
  const { lang, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState({
    fullName: '',
    address: '',
    city: '',
    phone: '',
    country: t('checkoutPage.countryMacedonia', 'Македонија')
  });
  const [selectedSize, setSelectedSize] = useState('M');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderProcessing, setOrderProcessing] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const shippingCost = 0;

  const fetchUserDetails = useCallback(async () => {
    try {
      const userData = await fetchWithAuth('/users/me');
      setDeliveryInfo((previous) => ({
        ...previous,
        fullName: userData.fullName || '',
        address: userData.address || '',
        city: userData.city || '',
        phone: userData.phone || '',
        country: userData.country || t('checkoutPage.countryMacedonia', 'Македонија')
      }));
    } catch (userError) {
      console.error('Error fetching user details:', userError);
      let storedUser = {};

      try {
        storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      } catch {
        storedUser = {};
      }

      setDeliveryInfo((previous) => ({
        ...previous,
        fullName: localStorage.getItem('userFullName') || '',
        address: storedUser.address || '',
        city: storedUser.city || '',
        phone: storedUser.phone || '',
        country: storedUser.country || t('checkoutPage.countryMacedonia', 'Македонија')
      }));
    }
  }, [t]);

  const fetchProductDetails = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWithAuth(`/products/${productId}`);
      setProduct(data);
      await fetchUserDetails();
      setError(null);
    } catch (productError) {
      console.error('Error fetching product details:', productError);
      setError(productError.message || t('checkoutPage.errorFetchingProduct'));
      toast.error(t('checkoutPage.errorFetchingProductToast'));
    } finally {
      setLoading(false);
    }
  }, [productId, fetchUserDetails, t]);

  useEffect(() => {
    trackVisit('CHECKOUT', productId);
    fetchProductDetails();
  }, [fetchProductDetails]);

  const handleDeliveryInfoChange = (event) => {
    const { name, value } = event.target;
    setDeliveryInfo((previous) => ({ ...previous, [name]: value }));
  };

  const subtotal = product?.price || 0;
  const total = subtotal + shippingCost;
  const formComplete = ['fullName', 'address', 'city', 'phone']
    .every((field) => deliveryInfo[field].trim());

  const handleActualOrderCreation = async () => {
    if (!product) return;

    setOrderProcessing(true);
    setError(null);

    try {
      const response = await fetchWithAuth('/orders', {
        method: 'POST',
        body: JSON.stringify({
          orderItems: [{ productId: product.id, quantity: 1 }],
          shippingAddress: `${deliveryInfo.address}, ${deliveryInfo.city}, ${deliveryInfo.country}`,
          productSize: selectedSize
        })
      });

      setShowConfirmationModal(false);
      toast.success(t('checkoutPage.orderSuccessToast'));
      navigate(`/${lang}/order-success`, {
        state: { orderId: response.id, orderData: response }
      });
    } catch (orderError) {
      console.error('Error creating order:', orderError);
      setError(orderError.message || t('checkoutPage.errorCreatingOrder'));
      toast.error(orderError.response?.data?.message || t('checkoutPage.errorCreatingOrderToast'));
      setShowConfirmationModal(false);
    } finally {
      setOrderProcessing(false);
    }
  };

  const resolveImageUrl = (imageUrl) => {
    if (!imageUrl) return '/placeholder-image.jpg';
    if (imageUrl.startsWith('http') || imageUrl.startsWith('/')) return imageUrl;
    return `/${imageUrl}`;
  };

  const deliveryFields = [
    {
      name: 'fullName',
      type: 'text',
      label: t('checkoutPage.fullNameLabel'),
      placeholder: t('checkoutPage.fullNamePlaceholder'),
      testId: 'checkout-fullname-input',
      wide: true
    },
    {
      name: 'address',
      type: 'text',
      label: t('checkoutPage.addressLabel'),
      placeholder: t('checkoutPage.addressPlaceholder'),
      testId: 'checkout-address-input',
      wide: true
    },
    {
      name: 'city',
      type: 'text',
      label: t('checkoutPage.cityLabel'),
      placeholder: t('checkoutPage.cityPlaceholder'),
      testId: 'checkout-city-input'
    },
    {
      name: 'phone',
      type: 'tel',
      label: t('checkoutPage.phoneLabel'),
      placeholder: t('checkoutPage.phonePlaceholder'),
      testId: 'checkout-phone-input'
    },
    {
      name: 'country',
      type: 'text',
      label: t('checkoutPage.countryLabel'),
      placeholder: '',
      testId: 'checkout-country-input',
      wide: true,
      readOnly: true
    }
  ];

  if (loading) {
    return (
      <div className="checkout-state" data-testid="checkout-page-loading">
        <RingLoader color="#9b6745" size={64} />
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="checkout-state">
        <div className="checkout-load-error" role="alert">
          <p>{error}</p>
          <button type="button" onClick={fetchProductDetails}>{t('productsPage.retry')}</button>
        </div>
      </div>
    );
  }

  if (!product) {
    return <div className="checkout-state"><p>{t('checkoutPage.productNotFound')}</p></div>;
  }

  return (
    <div className="checkout-page" data-testid="checkout-page">
      <header className="checkout-header">
        <div className="checkout-shell">
          <button
            type="button"
            className="checkout-back"
            onClick={() => navigate(`/${lang}/products`)}
          >
            <ArrowLeftIcon aria-hidden="true" />
            {t('checkoutPage.backToProducts')}
          </button>
          <p>{t('checkoutPage.eyebrow')}</p>
          <h1 data-testid="checkout-page-title">{t('checkoutPage.title')}</h1>
          <span>{t('checkoutPage.subtitle')}</span>
        </div>
      </header>

      <main className="checkout-shell checkout-layout">
        <aside className="checkout-product-card">
          <div className="checkout-product-visual">
            <img
              src={resolveImageUrl(product.imageUrl)}
              alt={t('productsPage.productName')}
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = '/placeholder-image.jpg';
              }}
              data-testid="checkout-product-image"
            />
          </div>

          <div className="checkout-product-copy">
            <p>{t('productsPage.productKicker')}</p>
            <h2 data-testid="checkout-product-name">{t('productsPage.productName')}</h2>
            <span data-testid="checkout-product-description">
              {t('productsPage.productSummary')}
            </span>
          </div>

          <div className="checkout-product-price">
            <span>{t('checkoutPage.orderSummaryTitle')}</span>
            <strong>{formatPrice(product.price, lang)}</strong>
          </div>
        </aside>

        <section className="checkout-form-card">
          <div className="checkout-form-section">
            <div className="checkout-section-heading">
              <span className="checkout-section-icon"><ShoppingBagIcon aria-hidden="true" /></span>
              <div>
                <h2>{t('sizePicker.title')}</h2>
                <p>{t('sizePicker.helpText')}</p>
              </div>
            </div>
            <SizePicker
              selectedSize={selectedSize}
              onSizeChange={setSelectedSize}
              disabled={orderProcessing}
            />
          </div>

          <div className="checkout-form-section checkout-delivery-section">
            <div className="checkout-section-heading">
              <span className="checkout-section-icon"><MapPinIcon aria-hidden="true" /></span>
              <div>
                <h2>{t('checkoutPage.deliveryInformationTitle')}</h2>
                <p>{t('checkoutPage.deliveryHint')}</p>
              </div>
            </div>

            <div className="checkout-fields">
              {deliveryFields.map((field) => (
                <div
                  key={field.name}
                  className={`checkout-field ${field.wide ? 'checkout-field-wide' : ''}`}
                >
                  <label htmlFor={field.name}>{field.label}</label>
                  <input
                    type={field.type}
                    name={field.name}
                    id={field.name}
                    value={deliveryInfo[field.name]}
                    onChange={handleDeliveryInfoChange}
                    placeholder={field.placeholder}
                    readOnly={field.readOnly}
                    disabled={orderProcessing}
                    data-testid={field.testId}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="checkout-pricing" data-testid="checkout-pricing-summary">
            <div>
              <span>{t('checkoutPage.subtotal')}</span>
              <strong data-testid="checkout-subtotal">{formatPrice(subtotal, lang)}</strong>
            </div>
            <div>
              <span>
                {t('checkoutPage.shippingCost')}
                <small>{t('checkoutPage.shippingCostPromo')}</small>
              </span>
              <strong data-testid="checkout-shipping-cost">{formatPrice(shippingCost, lang)}</strong>
            </div>
            <div className="checkout-total">
              <span>{t('checkoutPage.total')}</span>
              <strong data-testid="checkout-total">{formatPrice(total, lang)}</strong>
            </div>
          </div>

          {error && (
            <p className="checkout-inline-error" data-testid="checkout-error-message">{error}</p>
          )}

          <button
            type="button"
            onClick={() => setShowConfirmationModal(true)}
            disabled={orderProcessing || !formComplete}
            className="checkout-confirm-button"
            data-testid="checkout-confirm-button"
          >
            {orderProcessing ? (
              <>
                <RingLoader color="#ffffff" size={18} />
                {t('checkoutPage.processingButton')}
              </>
            ) : (
              <>
                <span>{t('checkoutPage.confirmOrderButton')}</span>
                <CheckCircleIcon aria-hidden="true" />
              </>
            )}
          </button>

          <p className="checkout-security-note">
            <ShieldCheckIcon aria-hidden="true" />
            {t('checkoutPage.secureNote')}
          </p>
        </section>
      </main>

      {showConfirmationModal && (
        <div className="checkout-modal-backdrop" data-testid="order-confirmation-modal">
          <div className="checkout-modal" role="dialog" aria-modal="true">
            <div className="checkout-modal-header">
              <div>
                <ExclamationTriangleIcon aria-hidden="true" />
                <h2 data-testid="order-confirmation-modal-title">
                  {t('checkoutPage.confirmOrderModalTitle')}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowConfirmationModal(false)}
                aria-label={t('common.close', 'Close')}
                data-testid="order-confirmation-modal-close"
              >
                <XMarkIcon aria-hidden="true" />
              </button>
            </div>

            <div className="checkout-modal-body">
              <p data-testid="order-confirmation-modal-message">
                {t('checkoutPage.confirmOrderModalMessage')}
              </p>

              <div className="checkout-modal-product">
                <img
                  src={resolveImageUrl(product.imageUrl)}
                  alt={t('productsPage.productName')}
                  data-testid="order-confirmation-product-image"
                />
                <div>
                  <strong data-testid="order-confirmation-product-name">
                    {t('productsPage.productName')}
                  </strong>
                  <span>{t('checkoutPage.quantityLabel')}: 1</span>
                  <span data-testid="order-confirmation-size">
                    {t('checkoutPage.sizeLabel')}: {selectedSize} ({selectedSize === 'S' ? '6x6 cm' : selectedSize === 'M' ? '8x8 cm' : '10x10 cm'})
                  </span>
                </div>
                <b data-testid="order-confirmation-product-price">
                  {formatPrice(product.price, lang)}
                </b>
              </div>

              <div className="checkout-modal-delivery">
                <h3>{t('checkoutPage.deliveryInfoSummaryTitle')}</h3>
                <dl>
                  <div>
                    <dt>{t('checkoutPage.fullNameLabel')}</dt>
                    <dd data-testid="order-confirmation-delivery-name">{deliveryInfo.fullName}</dd>
                  </div>
                  <div>
                    <dt>{t('checkoutPage.addressLabel')}</dt>
                    <dd data-testid="order-confirmation-delivery-address">{deliveryInfo.address}</dd>
                  </div>
                  <div>
                    <dt>{t('checkoutPage.cityLabel')}</dt>
                    <dd data-testid="order-confirmation-delivery-city">{deliveryInfo.city}</dd>
                  </div>
                  <div>
                    <dt>{t('checkoutPage.phoneLabel')}</dt>
                    <dd data-testid="order-confirmation-delivery-phone">{deliveryInfo.phone}</dd>
                  </div>
                </dl>
              </div>

              <div className="checkout-modal-total">
                <span>{t('checkoutPage.total')}</span>
                <strong data-testid="order-confirmation-total">{formatPrice(total, lang)}</strong>
              </div>

              <div className="checkout-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowConfirmationModal(false)}
                  disabled={orderProcessing}
                  data-testid="order-confirmation-cancel"
                >
                  {t('common.cancel', 'Откажи')}
                </button>
                <button
                  type="button"
                  onClick={handleActualOrderCreation}
                  disabled={orderProcessing}
                  data-testid="order-confirmation-confirm"
                >
                  {orderProcessing ? (
                    <>
                      <RingLoader color="#ffffff" size={15} />
                      {t('checkoutPage.processingButton')}
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon aria-hidden="true" />
                      {t('checkoutPage.confirmOrderFinalButton')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
